import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  CaretRight,
  Clock,
  MapPin,
  Sparkle,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  getAdmissionCapacity,
  getAdmissionWaitlistMatches,
  getAdmissionsDashboard,
  listAdmissionLocations,
} from "@/lib/admissions";

function localToday() {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
}

function formatDate(value, options = {}) {
  if (!value) return "—";
  // HTML date inputs and Postgres `date` values have no timezone. Parsing
  // `YYYY-MM-DD` directly treats it as UTC and can display the prior day for
  // a center west of Greenwich. Midday local time preserves the chosen
  // calendar date without changing timestamp behavior for tours.
  const source = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
  const date = new Date(source);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...options,
  });
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function countOf(source, ...keys) {
  return keys.reduce((total, key) => total + Number(source?.[key] || 0), 0);
}

/** Desktop-first operations hub. It surfaces explainable data and sends an
 * admin to the case file for every consequential action—no automatic offers. */
export default function AdminAdmissions() {
  const [startDate, setStartDate] = useState(localToday);
  const [locationId, setLocationId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [capacity, setCapacity] = useState([]);
  const [matches, setMatches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError("");
    try {
      const [nextDashboard, nextCapacity, nextMatches, nextLocations] = await Promise.all([
        getAdmissionsDashboard(),
        getAdmissionCapacity({ startDate, locationId }),
        getAdmissionWaitlistMatches({ startDate, locationId }),
        listAdmissionLocations(),
      ]);
      setDashboard(nextDashboard);
      setCapacity(nextCapacity);
      setMatches(nextMatches);
      setLocations(nextLocations);
    } catch (err) {
      const message = err?.message || "Could not load admissions operations.";
      setError(message);
      if (quiet) toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [locationId, startDate]);

  useEffect(() => {
    load();
  }, [load]);

  const attention = dashboard?.attention || {};
  const pipelineSummary = useMemo(() => {
    const pipeline = dashboard?.pipeline || {};
    return [
      { label: "Leads", value: countOf(pipeline, "lead", "inquiry", "tour_scheduled", "tour_completed"), tone: "sage" },
      { label: "Applications", value: countOf(pipeline, "application_started", "application_submitted"), tone: "slate" },
      { label: "Waitlist", value: countOf(pipeline, "waitlisted", "future_enrollment"), tone: "amber" },
      { label: "Offers", value: countOf(pipeline, "offer", "enrollment_pending", "accepted"), tone: "primary" },
    ];
  }, [dashboard?.pipeline]);

  return (
    <div data-testid="admin-admissions" className="space-y-6">
      <header className="flex flex-col gap-4 border-b border-neutral-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-hover">
            Admissions operations
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            What needs attention today?
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-neutral-600">
            See upcoming tours, real capacity signals, and waitlist recommendations in one place. HoneyAcorn suggests; authorized staff decide.
          </p>
        </div>
        <Link
          to="/admin/enrollment"
          data-testid="admissions-open-pipeline"
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-hover active:scale-[0.98]"
        >
          Open pipeline
          <ArrowRight size={15} weight="bold" />
        </Link>
      </header>

      <section
        aria-label="Admissions planning filters"
        className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-4 sm:flex-row sm:items-end"
      >
        <label className="min-w-0 flex-1 sm:max-w-52">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">Start date</span>
          <input
            data-testid="admissions-start-date"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            className="min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          />
        </label>
        <label className="min-w-0 flex-1 sm:max-w-64">
          <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.14em] text-neutral-500">Center</span>
          <select
            data-testid="admissions-location-filter"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
            className="min-h-10 w-full rounded-xl border border-neutral-200 bg-white px-3 text-sm text-neutral-900 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="">All centers</option>
            {locations.map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
          </select>
        </label>
        <button
          type="button"
          onClick={() => load({ quiet: true })}
          className="min-h-10 rounded-full border border-neutral-200 bg-white px-4 text-sm font-semibold text-neutral-700 transition hover:border-primary/40 hover:bg-primary/5"
        >
          Refresh
        </button>
      </section>

      {error && (
        <section data-testid="admissions-error" role="alert" className="rounded-2xl border border-[#C6851E]/40 bg-[#C6851E]/10 p-5">
          <div className="flex items-start gap-3">
            <WarningCircle size={20} weight="duotone" className="mt-0.5 text-[#8A5A12]" />
            <div>
              <p className="font-heading font-semibold text-neutral-900">Admissions data is unavailable</p>
              <p className="mt-1 text-sm text-neutral-700">{error}</p>
              <button type="button" onClick={() => load()} className="mt-3 text-sm font-semibold text-primary-hover hover:underline">Try again</button>
            </div>
          </div>
        </section>
      )}

      {loading && !dashboard ? (
        <LoadingState />
      ) : (
        <>
          <section aria-labelledby="admissions-attention-title" className="rounded-2xl border border-secondary/50 bg-secondary/20 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p id="admissions-attention-title" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-700">
                  <Sparkle size={16} weight="duotone" className="text-primary-hover" />
                  Needs attention
                </p>
                <p className="mt-1 text-sm text-neutral-600">Each signal links to the work; nothing here changes a family’s status automatically.</p>
              </div>
              {dashboard?.generated_at && <p className="text-xs text-neutral-500">Updated {formatDateTime(dashboard.generated_at)}</p>}
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <AttentionAction label="Tasks due" value={Number(attention.tasks_due || 0)} to="/admin/enrollment" />
              <AttentionAction label="Offers expiring soon" value={Number(attention.offers_expiring || 0)} to="/admin/enrollment" />
              <AttentionAction label="Unassigned leads" value={Number(attention.unassigned_leads || 0)} to="/admin/inquiries" />
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Admissions pipeline summary">
            {pipelineSummary.map((item) => <PipelineMetric key={item.label} {...item} />)}
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
            <UpcomingTours tours={dashboard?.upcoming_tours || []} />
            <CapacityPanel rows={capacity} startDate={startDate} />
          </section>

          <WaitlistPanel matches={matches} />
        </>
      )}
    </div>
  );
}

function AttentionAction({ label, value, to }) {
  return (
    <Link to={to} className="group flex min-h-24 items-center justify-between rounded-2xl border border-white/80 bg-white/85 p-4 shadow-sm transition hover:-translate-y-[1px] hover:border-primary/30 hover:shadow">
      <div>
        <p className="text-2xl font-heading font-semibold tracking-tight text-neutral-900">{value}</p>
        <p className="mt-1 text-sm font-medium text-neutral-700">{label}</p>
      </div>
      <CaretRight size={18} weight="bold" className="text-neutral-400 transition group-hover:translate-x-0.5 group-hover:text-primary-hover" />
    </Link>
  );
}

function PipelineMetric({ label, value, tone }) {
  const toneClass = {
    sage: "border-primary/20 bg-primary/5",
    slate: "border-neutral-200 bg-white",
    amber: "border-secondary/50 bg-secondary/20",
    primary: "border-primary/30 bg-primary text-white",
  }[tone];
  const labelClass = tone === "primary" ? "text-white/75" : "text-neutral-500";
  const valueClass = tone === "primary" ? "text-white" : "text-neutral-900";
  return (
    <div className={`rounded-2xl border p-5 ${toneClass}`}>
      <p className={`text-xs font-bold uppercase tracking-[0.14em] ${labelClass}`}>{label}</p>
      <p className={`mt-2 font-heading text-3xl font-semibold tracking-tight ${valueClass}`}>{value}</p>
    </div>
  );
}

function UpcomingTours({ tours }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" aria-labelledby="upcoming-tours-title">
      <header className="flex items-center justify-between gap-3 border-b border-neutral-100 px-5 py-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            <CalendarCheck size={16} weight="duotone" className="text-primary-hover" />
            Today & next
          </p>
          <h2 id="upcoming-tours-title" className="mt-1 font-heading text-lg font-semibold text-neutral-900">Upcoming tours</h2>
        </div>
        <Link to="/admin/enrollment" className="text-sm font-semibold text-primary-hover hover:underline">Pipeline</Link>
      </header>
      {tours.length === 0 ? (
        <div className="p-8 text-center">
          <CalendarCheck size={28} weight="duotone" className="mx-auto text-neutral-300" />
          <p className="mt-3 font-medium text-neutral-800">No tours scheduled</p>
          <p className="mt-1 text-sm text-neutral-500">Schedule one from a family’s admission case.</p>
        </div>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {tours.map((tour) => (
            <li key={tour.id}>
              <Link to={`/admin/enrollment/${tour.enrollment_id}`} className="flex items-center gap-3 px-5 py-4 transition hover:bg-neutral-50">
                <span className="flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
                  <span className="text-[10px] font-bold uppercase leading-none">{formatDate(tour.scheduled_at, { month: "short" }).slice(0, 3)}</span>
                  <span className="mt-0.5 text-sm font-heading font-semibold leading-none">{new Date(tour.scheduled_at).getDate()}</span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-heading text-sm font-semibold text-neutral-900">{tour.child_name}</span>
                  <span className="mt-0.5 block truncate text-xs text-neutral-600">{tour.parent_name}{tour.location_name ? ` · ${tour.location_name}` : ""}</span>
                </span>
                <span className="shrink-0 text-right text-xs font-medium text-neutral-600">{formatDateTime(tour.scheduled_at).split(",").slice(-1)[0]?.trim()}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CapacityPanel({ rows, startDate }) {
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-5" aria-labelledby="capacity-title">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            <UsersThree size={16} weight="duotone" className="text-primary-hover" />
            Planning signal
          </p>
          <h2 id="capacity-title" className="mt-1 font-heading text-lg font-semibold text-neutral-900">Capacity on {formatDate(startDate)}</h2>
        </div>
        <Link to="/admin/ratio-capacity" className="text-sm font-semibold text-primary-hover hover:underline">Ratios</Link>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-neutral-500">Planning guidance based on configured licensed capacity, enrolled children, and held offers. It is not a licensing determination.</p>
      <div className="mt-4 space-y-2">
        {rows.length === 0 ? (
          <EmptyInline text="No active classrooms match this center." />
        ) : rows.map((row) => {
          const isConfigured = row.out_status === "capacity_available";
          const status = isConfigured ? `${row.out_open_spots} open` : row.out_status === "needs_configuration" ? "Needs setup" : "Full";
          return (
            <div key={row.out_classroom_id} className="rounded-xl border border-neutral-100 bg-neutral-50/70 p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-neutral-900">{row.out_classroom_name}</p>
                  <p className="mt-0.5 truncate text-xs text-neutral-500">{row.out_location_name || "Center not assigned"}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-semibold ${isConfigured ? "bg-primary/10 text-primary-hover" : "bg-secondary/40 text-neutral-700"}`}>{status}</span>
              </div>
              <p className="mt-2 text-xs text-neutral-600">{row.out_current_enrolled} enrolled · {row.out_held_offers} held offer{row.out_held_offers === 1 ? "" : "s"} · licensed capacity {row.out_licensed_capacity ?? "not set"}</p>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function WaitlistPanel({ matches }) {
  return (
    <section className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" aria-labelledby="waitlist-title">
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-neutral-100 px-5 py-4">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
            <Clock size={16} weight="duotone" className="text-primary-hover" />
            Explainable recommendation
          </p>
          <h2 id="waitlist-title" className="mt-1 font-heading text-lg font-semibold text-neutral-900">Waitlist matches to review</h2>
          <p className="mt-1 text-xs text-neutral-500">Queue order uses your center priority, sibling connection, waitlist entry, then record creation. No offers are made automatically.</p>
        </div>
        <span className="rounded-full bg-secondary/25 px-3 py-1 text-xs font-semibold text-neutral-700">Human review required</span>
      </header>
      {matches.length === 0 ? (
        <div className="p-8 text-center">
          <MapPin size={28} weight="duotone" className="mx-auto text-neutral-300" />
          <p className="mt-3 font-medium text-neutral-800">No reviewed matches right now</p>
          <p className="mt-1 text-sm text-neutral-500">This can mean capacity is full, data needs configuration, or no waitlisted case fits the selected date.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-[720px] w-full text-left text-sm">
            <thead className="bg-neutral-50 text-[11px] font-bold uppercase tracking-[0.13em] text-neutral-500">
              <tr>
                <th className="px-5 py-3">Family</th>
                <th className="px-4 py-3">Classroom</th>
                <th className="px-4 py-3">Queue</th>
                <th className="px-4 py-3">Why matched</th>
                <th className="px-5 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {matches.map((match) => (
                <tr key={`${match.out_enrollment_id}-${match.out_classroom_id}`} className="hover:bg-neutral-50/70">
                  <td className="px-5 py-4"><p className="font-semibold text-neutral-900">{match.out_child_name}</p><p className="mt-0.5 text-xs text-neutral-500">{match.out_parent_name}</p></td>
                  <td className="px-4 py-4"><p className="text-neutral-800">{match.out_classroom_name}</p><p className="mt-0.5 text-xs text-neutral-500">{match.out_open_spots} open spot{match.out_open_spots === 1 ? "" : "s"}</p></td>
                  <td className="px-4 py-4"><span className="font-semibold text-neutral-800">#{match.out_rank}</span><span className="ml-2 text-xs text-neutral-500">priority {match.out_waitlist_priority}</span></td>
                  <td className="px-4 py-4 text-xs text-neutral-600">{match.out_evidence?.match_basis?.replaceAll("_", " ") || "Center queue rules"}{match.out_sibling_connected ? " · sibling connection" : ""}</td>
                  <td className="px-5 py-4 text-right"><Link to={`/admin/enrollment/${match.out_enrollment_id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-primary-hover hover:underline">Review case <CaretRight size={13} weight="bold" /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function EmptyInline({ text }) {
  return <p className="rounded-xl border border-dashed border-neutral-200 px-3 py-5 text-center text-sm text-neutral-500">{text}</p>;
}

function LoadingState() {
  return (
    <div data-testid="admissions-loading" className="space-y-4" aria-live="polite" aria-label="Loading admissions operations">
      <div className="h-32 animate-pulse rounded-2xl bg-neutral-100" />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{Array.from({ length: 4 }, (_, index) => <div key={index} className="h-28 animate-pulse rounded-2xl bg-neutral-100" />)}</div>
      <div className="grid gap-6 xl:grid-cols-2"><div className="h-80 animate-pulse rounded-2xl bg-neutral-100" /><div className="h-80 animate-pulse rounded-2xl bg-neutral-100" /></div>
    </div>
  );
}
